import { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
} from "@headlessui/react";
import { Button } from "../Button";
import { APIs } from "../../services/Apis";
import { toast } from "react-toastify";

const MIN_CHARS = 20;

export default function AddStakeholderDialog({
  open,
  setOpen,
  businessUrl,
  onSuccess,
}) {
  const [stakeholderName, setStakeholderName] = useState("");
  const [stakeholderDescription, setStakeholderDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const textareaRef = useRef(null);

  const handleClose = () => {
    if (!loading) {
      setStakeholderName("");
      setStakeholderDescription("");
      setOpen(false);
    }
  };

  // Auto-focus and auto-resize
  useEffect(() => {
    if (open && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [open]);

  const autoResize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 200) + "px";
  };

  const handleInput = (e) => {
    setStakeholderDescription(e.target.value);
    autoResize();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (canSubmit) {
        handleAddStakeholder();
      }
    }
  };

  const handleAddStakeholder = async () => {
    if (!stakeholderName.trim()) {
      toast.error("Please enter a stakeholder name");
      return;
    }
    if (!stakeholderDescription.trim()) {
      toast.error("Please enter a stakeholder description");
      return;
    }

    setLoading(true);
    try {
      const response = await APIs.addStakeholder(
        stakeholderName,
        stakeholderDescription,
        businessUrl,
      );

      if (response.success) {
        toast.success("Stakeholder created successfully!");
        setStakeholderName("");
        setStakeholderDescription("");
        setOpen(false);
        if (onSuccess) {
          onSuccess(response.stakeholderId);
        }
      } else {
        toast.error(response.message || "Failed to create stakeholder");
      }
    } catch (error) {
      console.error("Error adding stakeholder:", error);
    } finally {
      setLoading(false);
    }
  };

  const charCount = stakeholderDescription.trim().length;
  const canSubmit = stakeholderName.trim() && charCount >= MIN_CHARS && !loading;

  return (
    <Dialog open={open} onClose={handleClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-2xl data-closed:sm:translate-y-0 data-closed:sm:scale-95"
          >
            <div className="p-4 sm:p-6">
              {/* Header */}
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  New Stakeholder
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Describe the stakeholder group and AI will set up their
                  engagement goals
                </p>
              </div>

              {/* Stakeholder name */}
              <div className="mb-3">
                <label
                  htmlFor="stakeholder-name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Name
                </label>
                <input
                  id="stakeholder-name"
                  type="text"
                  value={stakeholderName}
                  onChange={(e) => setStakeholderName(e.target.value)}
                  placeholder="e.g., New Customers"
                  disabled={loading}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-300 focus:bg-white focus:outline-none disabled:text-gray-500 transition-colors"
                />
              </div>

              {/* Description textarea */}
              <label
                htmlFor="stakeholder-description"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Description
              </label>
              <div className="relative rounded-xl border border-gray-200 bg-gray-50 focus-within:border-gray-300 focus-within:bg-white transition-colors">
                <textarea
                  ref={textareaRef}
                  value={stakeholderDescription}
                  onChange={handleInput}
                  onKeyDown={handleKeyDown}
                  placeholder="e.g., Customers who signed up in the last 30 days but haven't made a purchase yet..."
                  disabled={loading}
                  rows={1}
                  className="w-full resize-none bg-transparent px-4 pt-4 pb-10 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none disabled:text-gray-500"
                  style={{ minHeight: "100px", maxHeight: "200px" }}
                />

                {/* Character counter / loading inside textarea bottom-left */}
                <div className="absolute bottom-0 left-0 px-3 py-2">
                  {loading ? (
                    <div className="flex items-center gap-2 text-xs text-primary-dark">
                      <svg
                        className="animate-spin h-3.5 w-3.5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span>Creating stakeholder...</span>
                    </div>
                  ) : (
                    <span
                      className={`text-xs ${
                        charCount > 0 && charCount < MIN_CHARS
                          ? "text-amber-500"
                          : "text-gray-400"
                      }`}
                    >
                      {charCount}/{MIN_CHARS} min characters
                    </span>
                  )}
                </div>
              </div>

              {/* Buttons row */}
              <div className="mt-3 flex items-center justify-end">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    color="slate"
                    size="md"
                    onClick={handleClose}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="solid"
                    color="primary"
                    size="md"
                    onClick={handleAddStakeholder}
                    disabled={!canSubmit}
                    loading={loading}
                  >
                    Create
                  </Button>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
